import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, Search, Download, Clock, Bot, Link2, BarChart3, CheckCircle, XCircle, Filter } from 'lucide-react';
import { StatusDot } from '@/components/shared/StatusDot';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const AGENTS = ['', 'MOA', 'ARIA', 'CHANGE-IQ', 'CONCIERGE', 'NOVA', 'ATLAS', 'CAPACITOR', 'HERALD', 'SENTINL', 'LEARN-X', 'COMPLIANCE-OPS'];
const SOURCES = [{ id: 'all', label: 'All' }, { id: 'immutable', label: 'Hash Chain' }, { id: 'regular', label: 'Regular' }];
const TAG_COLORS = { 'SOC2': '#06B6D4', 'ISO20000': '#3B82F6', 'HIPAA': '#EF4444', 'GDPR': '#10B981' };

export default function AuditTrailPage() {
  const token = localStorage.getItem('nexus_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [source, setSource] = useState('all');
  const [agent, setAgent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [complianceTag, setComplianceTag] = useState('');
  const [days, setDays] = useState(30);

  const { data: dashboard } = useQuery({
    queryKey: ['audit-dashboard'],
    queryFn: async () => { const { data } = await axios.get(`${BACKEND_URL}/api/audit/dashboard`, { headers }); return data; },
    refetchInterval: 30000,
  });

  const { data: entries, isLoading } = useQuery({
    queryKey: ['audit-unified', source, agent, searchTerm, complianceTag, days],
    queryFn: async () => {
      const params = new URLSearchParams({ source, days: days.toString(), limit: '200' });
      if (agent) params.set('agent', agent);
      if (searchTerm) params.set('search', searchTerm);
      if (complianceTag) params.set('compliance_tag', complianceTag);
      const { data } = await axios.get(`${BACKEND_URL}/api/audit/unified?${params}`, { headers });
      return data;
    },
    keepPreviousData: true,
  });

  const handleExport = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/audit/export?days=${days}`, { headers });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `nexus-audit-${days}d.json`; a.click();
      toast.success(`Exported ${data.immutable_entries + data.regular_entries} entries`);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div data-testid="audit-trail-page" className="w-full max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="font-heading text-xl font-semibold text-[var(--nexus-text-primary)]">Audit Trail</h1>
          <p className="text-xs text-[var(--nexus-text-muted)] font-mono mt-0.5">Unified view — {dashboard?.immutable_entries || 0} immutable + {dashboard?.regular_entries || 0} regular entries</p></div>
        <button onClick={handleExport} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0d3d4d] border border-cyan-500/30 text-[var(--nexus-accent)] text-xs font-semibold hover:bg-[#0f4a5c]">
          <Download size={12} /> Export JSON
        </button>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <StatBox label="Immutable Entries" value={dashboard.immutable_entries} color="var(--nexus-accent)" />
          <StatBox label="Today" value={dashboard.entries_today} color="var(--nexus-primary-bright)" />
          <StatBox label="Chain Health" value={dashboard.chain_health} color={dashboard.chain_health === 'VERIFIED' ? 'var(--nexus-success)' : 'var(--nexus-danger)'} icon={dashboard.chain_health === 'VERIFIED' ? CheckCircle : XCircle} />
          <StatBox label="Regular Entries" value={dashboard.regular_entries} color="var(--nexus-text-secondary)" />
          <StatBox label="Compliance Tags" value={Object.keys(dashboard.by_compliance_tag || {}).length} color="var(--nexus-warning)" />
        </div>
      )}

      {/* By Agent + By Tag */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="nexus-card p-3">
            <h3 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-2">Entries by Agent</h3>
            <div className="flex flex-wrap gap-1">{Object.entries(dashboard.by_agent || {}).map(([a, c]) => (
              <button key={a} onClick={() => setAgent(agent === a ? '' : a)}
                className={`font-mono text-[10px] px-2 py-0.5 rounded border ${agent === a ? 'bg-[#0d3d4d] text-[var(--nexus-accent)] border-cyan-500/30' : 'bg-[var(--nexus-bg-base)] text-[var(--nexus-text-secondary)] border-[var(--nexus-border)]'}`}>{a}: {c}</button>
            ))}</div>
          </div>
          <div className="nexus-card p-3">
            <h3 className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium mb-2">Compliance Tags</h3>
            <div className="flex flex-wrap gap-1">{Object.entries(dashboard.by_compliance_tag || {}).map(([tag, c]) => {
              const prefix = tag.split('_')[0];
              const color = TAG_COLORS[prefix] || 'var(--nexus-text-muted)';
              return <button key={tag} onClick={() => setComplianceTag(complianceTag === tag ? '' : tag)}
                className={`font-mono text-[9px] px-1.5 py-0.5 rounded border ${complianceTag === tag ? 'bg-[#0d2847] border-blue-500/30' : 'border-[var(--nexus-border)]'}`} style={{ color }}>{tag}: {c}</button>;
            })}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex gap-1">{SOURCES.map(s => (
          <button key={s.id} onClick={() => setSource(s.id)} className={`px-2.5 py-1 rounded text-[10px] font-medium border ${source === s.id ? 'bg-[#0d2847] text-[var(--nexus-primary-bright)] border-blue-500/30' : 'bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-muted)] border-[var(--nexus-border)]'}`}>{s.label}</button>
        ))}</div>
        <select value={agent} onChange={e => setAgent(e.target.value)} className="px-2.5 py-1 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] outline-none">
          <option value="">All Agents</option>{AGENTS.filter(Boolean).map(a => <option key={a} value={a}>{a}</option>)}</select>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} className="px-2.5 py-1 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] text-[10px] text-[var(--nexus-text-secondary)] outline-none">
          <option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option></select>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)]">
          <Search size={12} className="text-[var(--nexus-text-muted)]" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..."
            className="bg-transparent text-[10px] text-[var(--nexus-text-primary)] placeholder:text-[var(--nexus-text-muted)] outline-none w-32" />
        </div>
        {(agent || searchTerm || complianceTag) && <button onClick={() => { setAgent(''); setSearchTerm(''); setComplianceTag(''); }} className="text-[10px] text-[var(--nexus-accent)] hover:underline">Clear filters</button>}
      </div>

      {/* Entries */}
      <div className="nexus-card relative overflow-hidden">
        <ScrollArea className="h-[500px]">
          <div className="p-2 space-y-1" data-testid="audit-entries">
            {isLoading ? [...Array(10)].map((_, i) => <div key={i} className="h-10 rounded-lg bg-[var(--nexus-bg-base)] animate-pulse" />) :
            (entries || []).map((entry, i) => {
              const isImmutable = entry.audit_source === 'immutable';
              return (
                <div key={entry.id || i} className={`flex items-start gap-2 p-2 rounded-lg border ${isImmutable ? 'bg-[var(--nexus-bg-base)] border-[var(--nexus-border)]' : 'bg-[var(--nexus-bg-elevated)] border-transparent'}`}>
                  <div className="flex flex-col items-center flex-shrink-0 mt-0.5">
                    {isImmutable ? <Link2 size={10} className="text-[var(--nexus-accent)]" /> : <Clock size={10} className="text-[var(--nexus-text-muted)]" />}
                    {isImmutable && entry.sequence_number && <span className="font-mono text-[8px] text-[var(--nexus-accent)]">#{entry.sequence_number}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--nexus-bg-elevated)] text-[var(--nexus-text-secondary)] border border-[var(--nexus-border)] font-mono">{entry.action}</span>
                      {(entry.agent_name || entry.actor_id) && <span className="font-mono text-[10px] text-[var(--nexus-accent)]">{entry.agent_name || entry.actor_id}</span>}
                      {entry.entity_type && <span className="text-[9px] text-[var(--nexus-text-muted)]">{entry.entity_type}</span>}
                      {entry.entity_id && <span className="font-mono text-[9px] text-[var(--nexus-primary-bright)]">{entry.entity_id}</span>}
                      {(entry.compliance_tags || []).map(tag => {
                        const prefix = tag.split('_')[0];
                        return <span key={tag} className="font-mono text-[8px] px-1 rounded border" style={{ color: TAG_COLORS[prefix] || '#94A3B8', borderColor: `${TAG_COLORS[prefix] || '#94A3B8'}40` }}>{tag}</span>;
                      })}
                    </div>
                    {isImmutable && entry.current_hash && <span className="font-mono text-[8px] text-[var(--nexus-text-muted)] block mt-0.5">{entry.current_hash.slice(0, 32)}...</span>}
                  </div>
                  <span className="font-mono text-[9px] text-[var(--nexus-text-muted)] flex-shrink-0">{(entry.timestamp || '').slice(11, 19)}</span>
                </div>
              );
            })}
            {!isLoading && (!entries || entries.length === 0) && <div className="text-center py-8 text-[var(--nexus-text-muted)] text-xs">No audit entries match your filters</div>}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

const StatBox = ({ label, value, color, icon: Icon }) => (
  <div className="nexus-card p-3">
    <div className="flex items-center justify-between">
      <p className="text-[9px] text-[var(--nexus-text-muted)] uppercase tracking-wider">{label}</p>
      {Icon && <Icon size={14} style={{ color }} />}
    </div>
    <p className="font-heading text-lg font-semibold tabular-nums" style={{ color }}>{value}</p>
  </div>
);
